import { initializeApp } from "firebase/app";

import {
	getDocs,
	deleteDoc,
	updateDoc,
	collection,
	doc,
	where,
	query,
	initializeFirestore,
	onSnapshot,
	setDoc,
	type DocumentReference,
	type QueryFieldFilterConstraint,
	type Unsubscribe,
} from "firebase/firestore";
import {
	getFunctions,
	httpsCallable,
} from "firebase/functions";
import {
	getDownloadURL,
	getStorage,
	ref,
	uploadBytes,
	deleteObject,
} from "firebase/storage";

import { getAuth } from "firebase/auth";
import type { IVideo } from "../entities/video.entity";
import _ from "lodash";
import { lazyCategories } from "../hooks/useCategories";
import type { IGroup } from "../entities/group.entity";
import type { IPlayer } from "../entities/player.entity";
import type { IEmbed } from "../entities/embed.entity";
import type { ICategory } from "../entities/category.entity";
import type { IUser } from "../entities/user.entity";
import type { ILink } from "../entities/link.entity";
import { captureException } from "@sentry/react";

// Values come from .env.staging / .env.production depending on the build mode
// (vite build --mode staging|production). Fallbacks point to STAGING so that
// builds without env files (dev server, tests) can never touch production.
const env = import.meta.env;

export const firebaseApp = initializeApp({
	apiKey: env.VITE_FIREBASE_API_KEY || "AIzaSyCyrk784BZTmLW4erPgq4jNJJ6guKaw9cI",
	// authDomain must match the domain serving the app so the /__/auth
	// handlers provisioned by Firebase Hosting keep working on every domain
	authDomain: window.location.origin.includes("localhost")
		? "localhost:5173"
		: window.location.host,
	projectId: env.VITE_FIREBASE_PROJECT_ID || "condorflix-staging",
	storageBucket:
		env.VITE_FIREBASE_STORAGE_BUCKET || "condorflix-staging.firebasestorage.app",
	messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID || "473842492621",
	appId:
		env.VITE_FIREBASE_APP_ID || "1:473842492621:web:24e7a37fdd0bdd2b947584",
	// measurementId: "G-2X5V2LQH6Y" // TODO: add analytics
});

const db = initializeFirestore(firebaseApp, {});
export const storage = getStorage(firebaseApp);
// export const analytics = getAnalytics(firebaseApp)
export const functions = getFunctions(firebaseApp);
export const auth = getAuth(firebaseApp);

// if (window.location.hostname.includes("localhost")) {
// 	// Ref: https://firebase.google.com/docs/emulator-suite/connect_auth?hl=pt#web-modular-api
// 	connectAuthEmulator(auth, "http://localhost:9099");
// 	connectFirestoreEmulator(db, "localhost", 8080);
// 	connectStorageEmulator(storage, "localhost", 9199);
// 	connectFunctionsEmulator(functions, "localhost", 5001);
// }

export async function firebaseAddFirestore<T extends object>(
	colectionName: string,
	data: Omit<T, "id" | "createdAt"> & { id?: string; createdAt?: Date },
) {
	let nextDoc: DocumentReference;
	if (!data.id) {
		console.log("firebaseAddFirestore", { colectionName, data });
		nextDoc = doc(collection(db, colectionName));
		data.id = nextDoc.id;
	} else {
		nextDoc = doc(db, colectionName, data.id);
	}
	console.log("firebaseAddFirestore", { colectionName, data });
	data.createdAt = new Date();
	await setDoc(nextDoc, data);
	console.log("firebaseAddFirestore done", { colectionName, data });
	return data as T & { id: string };
}

export async function firebaseReadFirestore<T>(colectionName: string) {
	const querySnapshot = await getDocs(collection(db, colectionName));

	return querySnapshot.docs.map((doc) => ({
		...doc.data(),
		id: doc.id,
	})) as T[];
}

export async function getPlayerByPassword(password: string) {
	const playersMatched = await getDocs(
		query(collection(db, "players"), where("password", "==", password)),
	);

	if (playersMatched.docs.length === 1) {
		return playersMatched.docs[0].data() as IPlayer;
	}

	throw new PlayerNotFoundError("Senha inválida");
}

export async function validateWithRemotePassword(password: string) {
	console.log("validateWithRemotePassword", { password });
	try {
		const player = await getPlayerByPassword(password);

		console.log("player", player);

		if (player) {
			return true;
		}
	} catch (e) {
		captureException(e);
		console.log("validateWithRemotePassword error", e);
		console.error(e);
	}

	return false;
}

export function validateWithLocalPassword(password: string) {
	return window.localStorage.getItem("activationPassword") === password;
}

export async function validatePassword(password: string) {
	console.log("will validate password", { password });

	if (await validateWithRemotePassword(password)) {
		console.log("remote password is valid", { password });
		return true;
	}

	console.log("remote password is invalid", { password });

	if (validateWithLocalPassword(password)) {
		console.log("local password is valid", { password });
		return true;
	}

	console.log("local password is invalid", { password });

	if (password === "p8ftUSW2b1") {
		console.log("recover password is valid", { password });
		return true;
	}

	console.log("recover password is invalid", { password });

	return false;
}

export const playerActivationStore = {
	get isActivated() {
		return localStorage.getItem("activated") === "true";
	},
	get activationPassword() {
		return localStorage.getItem("activationPassword");
	},
	// Valida se ativação está completa e consistente
	get isValid() {
		const activated = localStorage.getItem("activated") === "true";
		const password = localStorage.getItem("activationPassword");
		return activated && password !== null && password !== "";
	},
	setActive(password: string) {
		localStorage.setItem("activated", "true");
		localStorage.setItem("activationPassword", password);
	},
	// Limpa ativação corrompida
	clear() {
		localStorage.removeItem("activated");
		localStorage.removeItem("activationPassword");
	},
};

export async function activatePlayer(password: string) {
	const passwordIsValid = await validatePassword(password);
	if (!passwordIsValid) {
		console.log("password is invalid", { password });
		throw new Error("Senha inválida");
	}
	console.log("password is valid", { password });
	playerActivationStore.setActive(password);
	return true;
}

export async function fetchCategoryVideos(q: { categoryId?: string }) {
	const filters: QueryFieldFilterConstraint[] = [];

	if (q.categoryId) {
		filters.push(where("category.id", "==", q.categoryId));
	}

	const data = await getDocs(query(collection(db, "videos"), ...filters));
	const categories = await lazyCategories();

	return _.chain(data.docs)
		.map((doc) => doc.data())
		.map((video) => {
			const category = categories.findCategoryById(video.category.id);
			const subcategory = categories.findSubCategoryById(video.subcategory.id);
			return {
				...video,
				category,
				subcategory,
			};
		})
		.groupBy("subcategory.id")
		.values()
		.value() as unknown as IVideo[][];
}

export async function firebaseDeleteFirestore(
	docId: string,
	colection: string,
	filesIds: string[],
) {
	console.log("firebaseDeleteFirestore", { docId, colection, filesIds });

	if (filesIds) {
		for (const fileId of filesIds) {
			const fileRef = ref(storage, fileId);
			await deleteObject(fileRef).catch((error) => {
				console.error(error);
				captureException(error);
				// fail silently
			});
		}
	}

	await deleteDoc(doc(db, colection, docId));
}

export async function firebaseUpdateFirestore<T extends { id: string }>(
	colection: string,
	data: T,
) {
	await updateDoc(doc(db, `${colection}/${data.id}`), data);
}

export async function firebaseAddStorage(
	path: string,
	data: File,
): Promise<{ id: string; url: string }> {
	const storageRef = ref(
		storage,
		`${path}/${data.name.replace(".", `-${Date.now()}.`)}`,
	);

	const response = {
		id: "",
		url: "",
	};

	const uploadedObject = await uploadBytes(storageRef, data);
	response.id = uploadedObject.ref.fullPath;
	response.url = await getDownloadURL(storageRef);

	return response;
}

export interface S3PresignedUrlResult {
	signedUpload: S3SignedLoad;
	signedDownload: S3SignedLoad;
}

export interface S3SignedLoad {
	url: string;
	method: string;
	headers: Headers;
	fullPath: string;
}

export async function createPresignedUrl(
	file: File,
	kind: "video" | "thumbnail",
) {
	const createPresignedUrl = httpsCallable(functions, "createPresignedUrl");

	console.log("createPresignedUrl", { file, kind });

	const response = await createPresignedUrl({
		fileName: file.name,
		fileSize: file.size,
		filePath: kind,
	});

	console.log("createPresignedUrl response", response);

	return response.data as S3PresignedUrlResult;
}

export function updateEmbed(data: IEmbed) {
	return firebaseUpdateFirestore("embeds", data);
}

export function createEmbed(data: Omit<IEmbed, "id" | "createdAt">) {
	return firebaseAddFirestore("embeds", { ...data, createdAt: new Date() });
}

export function listEmbeds() {
	return firebaseReadFirestore<IEmbed>("embeds");
}

export function deleteEmbedById(id: string) {
	return firebaseDeleteFirestore(id, "embeds", []);
}

export function listGroups() {
	return firebaseReadFirestore<IGroup>("groups");
}

export function listUsers() {
	return firebaseReadFirestore<IUser>("users");
}

// Content versions are published by the onContentChange_* Cloud Functions in a
// single document. Kiosks listen to that one document instead of polling the
// whole content (hundreds of reads per cycle), so a change made in the
// dashboard reaches every player within seconds.
const CONTENT_VERSION_DOCUMENT = "meta/contentVersion";
// Dedicated document used to force every kiosk to look for a new build right
// away, instead of waiting for the next service worker poll. It carries the
// same shape as the content version: { version: <epoch millis> }.
const APP_UPDATE_DOCUMENT = "meta/appUpdate";

// One Firestore listener per document per page, shared by every caller, so a TV
// keeps a single stream instead of one per component.
const createVersionSubscriber = (documentPath: string) => {
	const listeners = new Set<(version: number | null) => void>();
	let unsubscribe: Unsubscribe | null = null;
	let lastVersion: number | null | undefined;

	return (onChange: (version: number | null) => void) => {
		listeners.add(onChange);

		if (!unsubscribe) {
			lastVersion = undefined;
			unsubscribe = onSnapshot(
				doc(db, documentPath),
				(snapshot) => {
					const version = snapshot.exists()
						? Number(snapshot.get("version"))
						: null;

					// The first snapshot is the current state, not a change.
					if (lastVersion === undefined) {
						lastVersion = version;
						return;
					}

					if (version === lastVersion) return;
					lastVersion = version;

					for (const listener of listeners) {
						listener(version);
					}
				},
				(error) => captureException(error),
			);
		}

		return () => {
			listeners.delete(onChange);

			if (listeners.size === 0 && unsubscribe) {
				unsubscribe();
				unsubscribe = null;
			}
		};
	};
};

export const subscribeContentVersion = createVersionSubscriber(
	CONTENT_VERSION_DOCUMENT,
);

export const subscribeAppUpdate = createVersionSubscriber(
	APP_UPDATE_DOCUMENT,
);

export function deleteGroupById(id: string) {
	return firebaseDeleteFirestore(id, "groups", []);
}

export function updateGroupById(data: IGroup) {
	return firebaseUpdateFirestore("groups", data);
}

export function createGroup(data: IGroup) {
	return firebaseAddFirestore<IGroup>("groups", {
		...data,
		createdAt: new Date(),
	});
}

export function updatePlayer(data: IPlayer) {
	return firebaseUpdateFirestore("players", data);
}

export class PlayerAlreadyExistsError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "PlayerAlreadyExistsError";
	}
}

export class PlayerNotFoundError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "PlayerNotFoundError";
	}
}

export async function createPlayer(data: Omit<IPlayer, "id" | "createdAt">) {
	try {
		// Check if password is already being used
		await getPlayerByPassword(data.password);
		// If getPlayerByPassword doesn't throw an error, it means the password is already in use
		throw new PlayerAlreadyExistsError("Senha já está sendo utilizada por outro player");
	} catch (error) {
		// If error message is "Senha inválida", it means the password is not in use
		if (error instanceof PlayerNotFoundError) {
			return firebaseAddFirestore("players", { ...data, createdAt: new Date() });
		}
		// If it's another error, rethrow it
		throw error;
	}
}

export function listPlayers() {
	return firebaseReadFirestore<IPlayer>("players");
}

export function deletePlayerById(id: string) {
	return firebaseDeleteFirestore(id, "players", []);
}

export function createVideo(data: Omit<IVideo, "id" | "createdAt">) {
	return firebaseAddFirestore("videos", { ...data, createdAt: new Date() });
}

export function updateVideoById(data: IVideo) {
	return firebaseUpdateFirestore("videos", data);
}

export async function queryVideos(groups: string[] = []) {
	// return firebaseReadFirestore<IVideo>('videos');
	const filters: QueryFieldFilterConstraint[] = [];

	if (groups.length > 0) {
		filters.push(where("groups", "array-contains-any", groups));
	}

	const docs = await getDocs(query(collection(db, "videos"), ...filters));
	return docs.docs.map((doc) => doc.data()) as IVideo[];
}

export function deleteVideoById(id: string) {
	return firebaseDeleteFirestore(id, "videos", []);
}

export async function createUserAccess(email: string) {
	try {
		const docRef = await setDoc(doc(collection(db, "users"), email), {
			allowed: true,
		});
		console.log("Document written with ID: ", docRef);
	} catch (e) {
		captureException(e);
		console.error("Error adding document: ", e);
	}
}

export function listCategories(): ICategory[] | PromiseLike<ICategory[]> {
	return firebaseReadFirestore<ICategory>("categories");
}

export function deleteCategoryById(id: string) {
	return firebaseDeleteFirestore(id, "categories", []);
}

export function updateCategoryById(data: ICategory) {
	for (const subCategory of data.subCategories) {
		if (!subCategory.id) {
			subCategory.id = doc(collection(db, "subCategories")).id;
		}
	}

	return firebaseUpdateFirestore("categories", data);
}

export function createCategory(data: Omit<ICategory, "id" | "createdAt">) {
	for (const subCategory of data.subCategories) {
		if (!subCategory.id) {
			subCategory.id = doc(collection(db, "subCategories")).id;
		}
	}

	return firebaseAddFirestore<ICategory>("categories", {
		...data,
		createdAt: new Date(),
	});
}

export function deleteUserById(id: string) {
	return firebaseDeleteFirestore(id, "users", []);
}

export function updateCategory(data: ICategory) {
	return firebaseUpdateFirestore("categories", data);
}

// Link CRUD operations
export async function queryLinks(groups: string[] = []) {
	const filters: QueryFieldFilterConstraint[] = [];

	if (groups.length > 0) {
		filters.push(where("groups", "array-contains-any", groups));
	}
	const docs = await getDocs(query(collection(db, "links"), ...filters));
	return docs.docs.map((doc) => doc.data()) as ILink[];
}

export function createLink(data: Omit<ILink, "id" | "createdAt">) {
	return firebaseAddFirestore<ILink>("links", {
		...data,
		createdAt: new Date(),
	});
}

export function updateLink(data: ILink) {
	return firebaseUpdateFirestore("links", data);
}

export function deleteLinkById(id: string) {
	return firebaseDeleteFirestore(id, "links", []);
}
